import axios from 'axios';

const SPREADSHEET_ID = '1_w7CoAyoq25deLHi-p4AUrIjcooqhZWbEYKPXC5gbAw';

// 각 페이지별 gid 정의
const SHEET_GIDS = {
  notice: '0',           // 공지사항 (기본 시트)
  pressRelease: '18074581',  // 보도자료
  mediaCoverage: '301975294', // 언론보도
  resources: '586959298'      // 자료실
};

// CSV 방식으로 데이터 가져오기 (gid 매개변수 추가)
const getSheetDataAsCSV = async (gid = '0') => {
  try {
    const url = `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/gviz/tq?tqx=out:csv&gid=${gid}`;
    const response = await axios.get(url);
    return parseCSVData(response.data);
  } catch (error) {
    console.error('Error fetching CSV data:', error);
    return [];
  }
};

// CSV 라인을 파싱하는 함수 (따옴표와 줄바꿈 처리 포함)
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // 연속된 따옴표는 이스케이프된 따옴표
        current += '"';
        i++; // 다음 따옴표 건너뛰기
      } else {
        // 따옴표 시작/끝
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
};

// 개선된 CSV 데이터 파싱 함수
const parseCSVData = (csvData) => {
  console.log('Raw CSV Data:', csvData);
  
  const data = [];
  let currentLine = '';
  let inQuotes = false;
  let lines = [];
  
  // 줄바꿈을 고려한 라인 분할
  for (let i = 0; i < csvData.length; i++) {
    const char = csvData[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
      currentLine += char;
    } else if (char === '\n' && !inQuotes) {
      if (currentLine.trim()) {
        lines.push(currentLine);
      }
      currentLine = '';
    } else {
      currentLine += char;
    }
  }
  
  // 마지막 라인 추가
  if (currentLine.trim()) {
    lines.push(currentLine);
  }
  
  console.log('Parsed Lines:', lines);
  
  if (lines.length < 2) {
    return [];
  }
  
  // 헤더 파싱
  const headers = parseCSVLine(lines[0]);
  console.log('Headers:', headers);
  
  // 데이터 행 파싱
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    console.log(`Row ${i} values:`, values);
    
    const row = {};
    
    headers.forEach((header, index) => {
      row[header.trim()] = values[index] ? values[index].trim() : '';
    });
    
    row.id = i;
    console.log(`Row ${i} mapped:`, row);
    
    // 빈 행이 아닌 경우에만 추가
    if (Object.values(row).some(value => value && value !== '')) {
      data.push(row);
    }
  }
  
  console.log('Final CSV parsed data:', data);
  return data;
};

// 공지사항 데이터 가져오기 (CSV 방식 사용)
export const getNoticeData = async () => {
  return await getSheetDataAsCSV(SHEET_GIDS.notice);
};

// 보도자료 데이터 가져오기
export const getPressReleaseData = async () => {
  return await getSheetDataAsCSV(SHEET_GIDS.pressRelease);
};

// 언론보도 데이터 가져오기
export const getMediaCoverageData = async () => {
  return await getSheetDataAsCSV(SHEET_GIDS.mediaCoverage);
};

// 자료실 데이터 가져오기
export const getResourcesData = async () => {
  return await getSheetDataAsCSV(SHEET_GIDS.resources);
};

// 특정 공지사항 가져오기
export const getNoticeById = async (id) => {
  const notices = await getNoticeData();
  return notices.find(notice => notice.id === parseInt(id));
};

// 첨부파일 URL을 다운로드 가능한 형태로 변환
export const convertDriveUrl = (url) => {
  if (!url) return null;
  
  // 이미 다운로드 링크인 경우 그대로 반환
  if (url.includes('drive.google.com/uc?')) {
    return url;
  }
  
  // Google Drive 공유 링크를 다운로드 링크로 변환
  // 패턴 1: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
  let fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
  
  // 패턴 2: https://drive.google.com/open?id=FILE_ID
  if (!fileIdMatch) {
    fileIdMatch = url.match(/[?&]id=([a-zA-Z0-9-_]+)/);
  }
  
  // 패턴 3: https://drive.google.com/d/FILE_ID/
  if (!fileIdMatch) {
    fileIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
  }
  
  if (fileIdMatch) {
    const fileId = fileIdMatch[1];
    // 직접 다운로드 링크로 변환
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  
  // Google Drive 링크가 아닌 경우 원본 URL 반환
  return url;
};

// 날짜 포맷팅
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

// 첨부파일 URL에서 파일 제목 추출
export const getFileNameFromUrl = async (url) => {
  if (!url) return null;
  
  try {
    // Google Drive 링크인 경우
    if (url.includes('drive.google.com')) {
      const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (fileIdMatch) {
        const fileId = fileIdMatch[1];
        // Google Drive API를 사용하여 파일 정보 가져오기 (공개 파일)
        const apiUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
        
        try {
          const response = await fetch(apiUrl, { method: 'HEAD' });
          const contentDisposition = response.headers.get('content-disposition');
          
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              return filenameMatch[1].replace(/['"]/g, '');
            }
          }
        } catch (error) {
          console.log('Could not fetch file name from Drive API');
        }
        
        // 파일 ID로부터 추정되는 이름 반환
        return `첨부파일_${fileId.substring(0, 8)}`;
      }
    }
    
    // 일반 URL인 경우 URL에서 파일명 추출
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    
    if (fileName && fileName.includes('.')) {
      return decodeURIComponent(fileName);
    }
    
    // 기본값
    return '첨부파일';
  } catch (error) {
    console.error('Error extracting filename:', error);
    return '첨부파일';
  }
}; 